import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { clearPersistedState, exportState, importState } from '../../store/middleware/persistenceMiddleware';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const PersistenceControls = () => {
  const [showControls, setShowControls] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  
  // Get current state for display
  const allState = useSelector(state => state);
  
  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all saved data? This will reset everything to default values.')) {
      clearPersistedState();
      window.location.reload();
    }
  };
  
  const handleExportData = () => {
    exportState();
    setImportStatus('exported');
    setTimeout(() => setImportStatus(null), 3000);
  };
  
  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (file) {
      importState(file)
        .then(() => {
          setImportStatus('imported');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        })
        .catch(err => {
          console.error('Import failed:', err);
          setImportStatus('error');
          setTimeout(() => setImportStatus(null), 3000);
        });
    }
  };
  
  const handlePublishChanges = () => {
    // Trigger a rebuild by updating localStorage timestamp
    const timestamp = Date.now();
    localStorage.setItem('portfolio_last_update', timestamp.toString());
    
    // Force a hard refresh to reflect changes
    if (window.confirm('Changes have been saved! To see them on the visitor page, we need to refresh. Click OK to refresh now.')) {
      window.location.href = window.location.origin;
    }
  };
  
  const getDataStats = () => {
    return {
      projects: allState.projects?.projects?.length || 0,
      skills: (allState.skills?.technicalSkills?.length || 0) + (allState.skills?.softSkills?.length || 0),
      experience: (allState.experience?.workExperience?.length || 0) + 
                 (allState.experience?.internshipsAndLearning?.length || 0) + 
                 (allState.experience?.achievements?.length || 0),
      certifications: allState.certifications?.certifications?.length || 0,
    };
  };
  
  const stats = getDataStats();
  
  return (
    <div className="bg-surface rounded-lg p-6 security-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Data Management
        </h3>
        <Button
          variant="outline"
          size="sm"
          iconName={showControls ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          onClick={() => setShowControls(!showControls)}
        >
          {showControls ? 'Hide' : 'Show'} Controls
        </Button>
      </div>
      
      {/* Data Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-primary">{stats.projects}</div>
          <div className="text-xs text-text-secondary">Projects</div>
        </div>
        <div className="text-center p-3 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-secondary">{stats.skills}</div>
          <div className="text-xs text-text-secondary">Skills</div>
        </div>
        <div className="text-center p-3 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-accent">{stats.experience}</div>
          <div className="text-xs text-text-secondary">Experience</div>
        </div>
        <div className="text-center p-3 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-primary">{stats.certifications}</div>
          <div className="text-xs text-text-secondary">Certificates</div>
        </div>
      </div>
      
      {/* Publish Button - Always Visible */}
      <div className="mb-4">
        <Button
          variant="default"
          size="lg"
          iconName="Upload"
          iconPosition="left"
          fullWidth
          className="cyber-gradient"
          onClick={handlePublishChanges}
        >
          Publish Changes to Visitor Site
        </Button>
        <p className="text-xs text-text-secondary mt-2 text-center">
          Click this after making changes to update the visitor-facing website
        </p>
      </div>
      
      {showControls && (
        <div className="space-y-4">
          {/* Status Messages */}
          {importStatus && (
            <div className={`flex items-center p-3 rounded-lg ${
              importStatus === 'exported' ? 'bg-accent/10 border border-accent/20' :
              importStatus === 'imported' ? 'bg-accent/10 border border-accent/20' :
              'bg-red-500/10 border border-red-500/20'
            }`}>
              <Icon 
                name={importStatus === 'error' ? 'AlertCircle' : 'CheckCircle'} 
                size={16} 
                className={`mr-2 ${importStatus === 'error' ? 'text-red-500' : 'text-accent'}`} 
              />
              <span className={`text-sm ${importStatus === 'error' ? 'text-red-500' : 'text-accent'}`}>
                {importStatus === 'exported' && 'Data exported successfully!'}
                {importStatus === 'imported' && 'Data imported successfully! Refreshing...'}
                {importStatus === 'error' && 'Import failed. Please check the file format.'}
              </span>
            </div>
          )}
          
          {/* Export/Import Controls */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                fullWidth
                onClick={handleExportData}
              >
                Export Backup
              </Button>
              <p className="text-xs text-text-secondary mt-1">
                Download current data as JSON backup
              </p>
            </div>
            
            <div>
              <input
                type="file"
                id="import-file"
                accept=".json"
                onChange={handleImportData}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                fullWidth
                onClick={() => document.getElementById('import-file').click()}
              >
                Import Backup
              </Button>
              <p className="text-xs text-text-secondary mt-1">
                Restore data from JSON backup file
              </p>
            </div>
          </div>
          
          {/* Danger Zone */}
          <div className="border-t border-border pt-4">
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center">
              <Icon name="AlertTriangle" size={16} className="text-orange-500 mr-2" />
              Danger Zone
            </h4>
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              fullWidth
              className="border-red-500/20 text-red-500 hover:bg-red-500/10"
              onClick={handleClearData}
            >
              Reset All Data
            </Button>
            <p className="text-xs text-red-500 mt-1">
              This will permanently delete all changes and reset to defaults
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersistenceControls; 